import Component from '@glimmer/component';
import type { Dayjs } from 'dayjs';
import { getLocalTime } from 'frontend-utils/utils/dayjs';
import HotelTooltipsTooltipRateAllchannels from 'lumen/components/hotel/tooltips/tooltip-rate-allchannels';
import HotelTooltipsTooltipRateRoomtype from 'lumen/components/hotel/tooltips/tooltip-rate-roomtype';
import type { PeriodParity, PeriodRate } from 'lumen/models/period';
import type { HotelOrMarket } from 'lumen/services/hotel-and-market';
import type { requestParams } from 'lumen/utils/constants/rate-constants';

interface HotelTooltipsTooltipRatesTableSignature {
  Args: {
    date?: Dayjs;
    displayOverModal?: boolean;
    fetchOtherLosRates?: boolean;
    hotel: HotelOrMarket;
    isAllChannels?: boolean;
    isParityAnalytics?: boolean;
    isUpdating?: boolean;
    noRate?: boolean;
    ownHotelRate?: PeriodRate | null;
    requestParams?: requestParams;
    value: PeriodRate | PeriodRate[] | PeriodParity | undefined;
  };
}

export default class HotelTooltipsTooltipRatesTable extends Component<HotelTooltipsTooltipRatesTableSignature> {
  get allChannelsValues(): PeriodRate[] {
    return this.args.isAllChannels ? (this.args.value as PeriodRate[]) : [];
  }

  get hasRate(): boolean {
    if (this.args.noRate || !this.args.value) {
      return false;
    }

    if (this.args.isAllChannels) {
      return true;
    }

    if (!Array.isArray(this.args.value)) {
      return this.args.value.value !== 0;
    }

    return false;
  }

  get hasTooltip(): boolean {
    // Table always has a tooltip if fromDate is today or in the future
    // (update time), or has a rate (roomtype etc.)
    // or this is the PA historical (also supports cells in the past)
    return (
      this.isFuture || this.hasRate || Boolean(this.args.isParityAnalytics)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.args.hotel;
  }

  get isFuture(): boolean {
    return Boolean(
      this.args.date &&
      (this.args.date.isAfter(
        getLocalTime(this.hotel.hotelinfo?.timezone).startOf('day'),
      ) ||
        this.args.date.isSame(
          getLocalTime(this.hotel.hotelinfo?.timezone).startOf('day'),
          'day',
        )),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get roomTypeValue() {
    return this.args.value as PeriodRate | PeriodParity | undefined;
  }

  <template>
    {{#if this.hasTooltip}}
      {{#if @isAllChannels}}
        <HotelTooltipsTooltipRateAllchannels
          @displayOverModal={{@displayOverModal}}
          @hotel={{this.hotel}}
          @isFuture={{this.isFuture}}
          @isUpdating={{@isUpdating}}
          @requestParams={{@requestParams}}
          @value={{this.allChannelsValues}}
        />
      {{else}}
        <HotelTooltipsTooltipRateRoomtype
          @displayOverModal={{@displayOverModal}}
          @extractDatetime={{this.roomTypeValue.extract_datetime}}
          @fetchOtherLosRates={{@fetchOtherLosRates}}
          @hasRate={{this.hasRate}}
          @hotel={{this.hotel}}
          @isFuture={{this.isFuture}}
          @isParityAnalytics={{@isParityAnalytics}}
          @isUpdating={{@isUpdating}}
          @ownHotelRate={{@ownHotelRate}}
          @requestParams={{@requestParams}}
          @value={{this.roomTypeValue}}
        />
      {{/if}}
    {{/if}}
  </template>
}
